/* eslint-disable @silverhand/fp/no-mutation -- Disposable effect state correlates one frame and its last measured height. */
import { useEffect, useRef } from 'react';

const allowedParent = (value: unknown): value is string => value === 'https://test.vibehub.pub' || value === 'https://vibehub.pub';
const uuid = /^[\da-f]{8}-(?:[\da-f]{4}-){3}[\da-f]{12}$/;

/** Presentation handshake only; never an authentication result or page selector. */
export const contextualFrameAttempt = (
  event: Pick<MessageEvent, 'origin' | 'source'> & { data: unknown },
  parent: Window,
  expectedOrigin = 'https://test.vibehub.pub'
): string | undefined => {
  const { data } = event;
  if (
    !allowedParent(expectedOrigin) || event.origin !== expectedOrigin ||
    event.source !== parent ||
    typeof data !== 'object' ||
    data === null ||
    Array.isArray(data) ||
    Object.keys(data).slice().sort().join(',') !== 'attempt_id,type' ||
    !('type' in data) ||
    data.type !== 'vh.auth.ready' ||
    !('attempt_id' in data) ||
    typeof data.attempt_id !== 'string' ||
    !uuid.test(data.attempt_id)
  ) {
    return;
  }
  return data.attempt_id;
};

const useContextualSignInFrame = (enabled: boolean, allowedOrigin?: string) => {
  const content = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = content.current;
    const { parent } = window;
    if (!enabled || !allowedParent(allowedOrigin) || parent === window || !element) {
      return;
    }
    const state: { attempt?: string; previousHeight?: number } = {};
    const size = () => {
      if (!state.attempt) {
        return;
      }
      // Measure natural content, not the viewport-height canvas, to allow shrink.
      const height = Math.min(
        1200,
        Math.max(240, Math.ceil(element.getBoundingClientRect().height + 8))
      );
      if (height !== state.previousHeight) {
        parent.postMessage(
          { type: 'vh.auth.resize', attempt_id: state.attempt, height },
          allowedOrigin
        );
        state.previousHeight = height;
      }
    };
    const receive = (event: MessageEvent) => {
      const id = contextualFrameAttempt(event, parent, allowedOrigin);
      if (!id || (state.attempt !== undefined && id !== state.attempt)) {
        return;
      }
      state.attempt = id;
      parent.postMessage({ type: 'vh.auth.ready', attempt_id: state.attempt }, allowedOrigin);
      size();
    };
    const escape = (event: KeyboardEvent) => {
      if (
        state.attempt &&
        event.key === 'Escape' &&
        !event.defaultPrevented &&
        !event.isComposing
      ) {
        event.preventDefault();
        parent.postMessage({ type: 'vh.auth.close', attempt_id: state.attempt }, allowedOrigin);
      }
    };
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(size);
    observer?.observe(element);
    window.addEventListener('message', receive);
    window.addEventListener('keydown', escape);
    window.addEventListener('resize', size);
    return () => {
      observer?.disconnect();
      window.removeEventListener('message', receive);
      window.removeEventListener('keydown', escape);
      window.removeEventListener('resize', size);
    };
  }, [enabled, allowedOrigin]);
  return content;
};

export default useContextualSignInFrame;

/* eslint-enable @silverhand/fp/no-mutation */
